// client/src/routes/app/contracts/$contractId/amendments/new.tsx — SCR-CON-004 · CON-005
// (04 §8.4's route table, which spells this URL out).
//
// `Leasing.Write`, and one more guard the permission cannot express: CON-001 accepts an act
// adițional only on an ACTIVE contract (422 `contract-not-amendable`). A draft is edited in
// place and a terminated contract is history, so both are sent back to the detail with the
// server's own sentence rather than shown a wizard whose submit cannot succeed.
//
// The wizard lands on the „Acte adiționale" tab — the act is now a row in the timeline, and the
// terms it moved are already on the Termeni tab, because CON-001 applies the patch to the row
// at write time.

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '@/components/layout/PageHeader';
import { RequirePermission } from '@/components/layout/route-guards';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { AmendmentWizard } from '@/features/contracts/AmendmentWizard';
import { toAmendmentInput, type AmendmentFormValues } from '@/features/contracts/amendment-schema';
import { useContract, useCreateAmendment } from '@/features/contracts/queries';
import { toast } from '@/stores/toast-store';

export const Route = createFileRoute('/app/contracts/$contractId/amendments/new')({
  component: NewAmendmentScreen,
});

function NewAmendmentScreen() {
  const { contractId } = Route.useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const contract = useContract(contractId);
  const create = useCreateAmendment();

  if (contract.isPending) return <LoadingState rows={4} />;
  if (contract.isError)
    return <ErrorState error={contract.error} onRetry={() => void contract.refetch()} />;

  const detail = contract.data;

  const back = async () => {
    await navigate({ to: '/app/contracts/$contractId/amendments', params: { contractId } });
  };

  if (detail.status !== 'ACTIVE') {
    return (
      <>
        <PageHeader
          title={t('contracts.amendmentWizard.title')}
          description={detail.contractNumber}
        />
        <div className="alert alert-warning text-sm" role="alert">
          {t('contracts.errors.notAmendable')}
        </div>
      </>
    );
  }

  return (
    <RequirePermission permission="Leasing.Write">
      <PageHeader
        title={t('contracts.amendmentWizard.title')}
        description={t('contracts.amendmentWizard.lead', { number: detail.contractNumber })}
        backTo="/app/contracts/$contractId/amendments"
        backLabel={t('contracts.detail.tabs.amendments')}
      />
      <AmendmentWizard
        contract={detail}
        onSubmit={async (values: AmendmentFormValues) => {
          await create.mutateAsync({ path: { id: contractId }, body: toAmendmentInput(values) });
          toast.success(t('contracts.amendmentWizard.created'));
          await back();
        }}
        onCancel={() => {
          void back();
        }}
      />
    </RequirePermission>
  );
}
